import { useInfiniteList } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { useSortedAssets } from "pages/asset-libraries/edit/";
import { CACHE_TIME_ASSETS } from "pages/media/components/storyboardEditor/constants";
import { MediaAsset } from "pages/media/types/";
import { useEffect, useMemo, useState } from "react";

const ASSETS_PAGE_SIZE = 10;

export const useLoadAllAssets = ({
  currentAssetLibraryId,
  staleTime = CACHE_TIME_ASSETS,
}: {
  currentAssetLibraryId: string;
  staleTime?: number;
}) => {
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const queryClient = useQueryClient();
  const queryKey = ["assetsList", currentAssetLibraryId];

  const {
    data: assetsData,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteList<MediaAsset>({
    resource: `media/asset_libraries/${currentAssetLibraryId}/assets`,
    queryOptions: {
      enabled: !!currentAssetLibraryId,
      staleTime,
      getNextPageParam: (lastPage, allPages) => {
        // If we received fewer items than the page size, there are no more pages
        if (lastPage.data.length < ASSETS_PAGE_SIZE) {
          return undefined;
        }
        return allPages.length + 1;
      },
      // use same key that useMediaLibraryAssets uses to share the same state and cache
      // it will allow optimistic update on asset upload for instance (font, etc)
      // TODO : refactor with 1 hook
      queryKey: ["assetsList", currentAssetLibraryId],
      refetchOnWindowFocus: false,
    },
    pagination: {
      pageSize: ASSETS_PAGE_SIZE,
    },
  });

  // Trigger loading more when initial load is complete
  useEffect(() => {
    if (currentAssetLibraryId && !isLoading && hasNextPage && !shouldLoadMore) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShouldLoadMore(true);
    }
  }, [currentAssetLibraryId, isLoading, hasNextPage, shouldLoadMore]);

  // Load next page when shouldLoadMore is true
  useEffect(() => {
    if (shouldLoadMore && hasNextPage && !isFetchingNextPage) {
      console.debug("fetching next page");
      fetchNextPage()
        .then(() => {
          // If there are still more pages, keep loading
          if (hasNextPage) {
            setShouldLoadMore(true);
          } else {
            setShouldLoadMore(false);
          }
        })
        .catch((error) => console.error("Error fetching next page", error));
    }
  }, [shouldLoadMore, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const updateAllLoadedAssetsOptimistically = (
    assets: Partial<MediaAsset>[]
  ) => {
    // Filter out any invalid assets
    const validAssets = assets.filter(
      (asset): asset is MediaAsset => asset.id != null
    );
    if (validAssets.length === 0) {
      return;
    }

    queryClient.setQueryData<typeof assetsData>(queryKey, (oldData) => {
      // Handle case when there's no existing data (first upload)
      if (!oldData?.pages.length) {
        return {
          pages: [{ data: validAssets, total: validAssets.length }],
          pageParams: [undefined],
        };
      }

      // Add new assets to the first page, avoiding duplicates
      const existingIds = new Set(
        oldData.pages[0]?.data.map((asset) => asset.id).filter(Boolean) ?? []
      );
      const newAssets = validAssets.filter(
        (asset) => asset.id && !existingIds.has(asset.id)
      );

      if (newAssets.length === 0) {
        return oldData;
      }

      const newPages = oldData.pages.map((page, index) => {
        if (index === 0) {
          return {
            ...page,
            data: [...newAssets, ...page.data],
            total: page.total + newAssets.length,
          };
        }
        return page;
      });

      return {
        ...oldData,
        pages: newPages,
      };
    });
  };

  const deleteAssetOptimistically = (assetId: string) => {
    queryClient.setQueryData<typeof assetsData>(queryKey, (oldData) => {
      const newPages = oldData?.pages.map((page) => {
        return {
          ...page,
          data: page.data.filter((asset) => asset.id !== assetId),
          total: page.total - 1,
        };
      });
      return {
        ...oldData,
        pages: newPages,
      };
    });
  };

  const assets = useMemo(() => {
    return assetsData?.pages.flatMap((page) => page.data) ?? [];
  }, [assetsData?.pages]);

  const { fonts, images, videos } = useSortedAssets(assets);

  return {
    assetsData,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    fonts,
    images,
    videos,
    updateAllLoadedAssetsOptimistically,
    deleteAssetOptimistically,
  };
};
